<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

// Live "what the customer receives" mockup, updated as the form is edited.
// Rendered in a fixed light chat look on purpose — it mimics the customer's
// phone, not the agent's dashboard theme.
const props = defineProps({
  content: { type: String, default: '' },
  attachments: { type: Array, default: () => [] },
  buttonLabel: { type: String, default: '' },
  buttonReplyContent: { type: String, default: '' },
  buttonAttachments: { type: Array, default: () => [] },
});

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

// Same markdown → HTML the real bubbles use, so links, bold and line breaks
// look like the customer's phone instead of raw markdown.
const render = txt => formatMessage(txt || '', false, false, false);

const kindOf = file => {
  const type = file?.contentType || file?.content_type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  return 'file';
};
const iconOf = file =>
  ({
    image: 'i-lucide-image',
    video: 'i-lucide-video',
    file: 'i-lucide-file-text',
  })[kindOf(file)];

const hasButton = computed(() => Boolean(props.buttonLabel.trim()));
const hasMessage = computed(
  () => Boolean(props.content.trim()) || props.attachments.length > 0
);
const replyFile = computed(() => props.buttonAttachments[0] || null);

const BUBBLE =
  'whitespace-pre-wrap break-words rounded-2xl rounded-tr-md bg-white px-2.5 py-1.5 text-[11.5px] leading-[1.7] text-[#111b21] shadow-sm [&_p]:mb-1 [&_p:last-child]:mb-0 [&_a]:text-[#027eb5] [&_a]:underline [&_a]:break-all';
</script>

<template>
  <div
    class="flex flex-col overflow-hidden rounded-[22px] border border-n-weak bg-[#efeae2] shadow-sm"
    dir="rtl"
  >
    <!-- phone header -->
    <div class="flex items-center gap-2 bg-[#075e54] px-3 py-2 text-white">
      <div
        class="grid size-7 place-items-center rounded-full bg-white/25 text-[12px] font-extrabold"
      >
        ت
      </div>
      <div class="leading-tight">
        <div class="text-[12px] font-bold">
          {{ t('CANNED_MGMT.PREVIEW.PAGE_NAME') }}
        </div>
        <div class="text-[10px] opacity-80">
          {{ t('CANNED_MGMT.PREVIEW.SUBTITLE') }}
        </div>
      </div>
    </div>

    <div class="flex max-h-[30rem] min-h-[14rem] flex-col gap-1.5 overflow-y-auto p-2.5">
      <!-- empty state -->
      <p
        v-if="!hasMessage"
        class="m-auto max-w-[80%] text-center text-[11.5px] font-semibold leading-relaxed text-[#6b7280]"
      >
        {{ t('CANNED_MGMT.PREVIEW.EMPTY') }}
      </p>

      <template v-else>
        <!-- the agent's message -->
        <div class="flex max-w-[90%] flex-col gap-1 self-start">
          <div
            v-for="file in attachments"
            :key="file.blobSignedId || file.blobId || file.filename"
            class="overflow-hidden rounded-2xl rounded-tr-md bg-white shadow-sm"
          >
            <img
              v-if="kindOf(file) === 'image' && file.url"
              :src="file.url"
              class="max-h-32 w-full object-cover"
              alt=""
            />
            <div
              v-else
              class="flex items-center gap-2 px-2.5 py-2 text-[11.5px] font-semibold text-[#111b21]"
            >
              <span
                class="grid size-7 shrink-0 place-items-center rounded-lg bg-[#e7f3ef] text-[#128c7e]"
              >
                <span :class="iconOf(file)" class="size-3.5" />
              </span>
              <span class="truncate" dir="ltr">{{ file.filename }}</span>
            </div>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-if="content.trim()" :class="BUBBLE" v-html="render(content)" />

          <!-- the interactive button -->
          <div
            v-if="hasButton"
            class="overflow-hidden rounded-xl bg-white shadow-sm"
          >
            <div
              class="flex items-center justify-center gap-1.5 p-2 text-[11.5px] font-bold text-[#1da5e0]"
            >
              <span class="i-lucide-corner-down-left size-3" />
              {{ buttonLabel }}
            </div>
          </div>
        </div>

        <!-- after the tap -->
        <template v-if="hasButton">
          <div
            class="my-0.5 flex items-center gap-2 text-[10px] font-extrabold text-[#54656f]"
          >
            <span class="h-px flex-1 bg-[#d9d2c7]" />
            {{ t('CANNED_MGMT.PREVIEW.AFTER_TAP') }}
            <span class="h-px flex-1 bg-[#d9d2c7]" />
          </div>

          <div
            class="max-w-[84%] self-end rounded-2xl rounded-tl-md bg-[#d9fdd3] px-2.5 py-1.5 text-[11.5px] font-semibold text-[#111b21] shadow-sm"
          >
            {{ buttonLabel }}
          </div>

          <div class="flex max-w-[90%] flex-col gap-1 self-start">
            <div
              v-if="replyFile"
              class="overflow-hidden rounded-2xl rounded-tr-md bg-white shadow-sm"
            >
              <img
                v-if="kindOf(replyFile) === 'image' && replyFile.url"
                :src="replyFile.url"
                class="max-h-32 w-full object-cover"
                alt=""
              />
              <div
                v-else-if="kindOf(replyFile) === 'video'"
                class="grid h-20 w-44 place-items-center bg-[#111b21]"
              >
                <span
                  class="grid size-9 place-items-center rounded-full bg-white/90 text-[#111b21]"
                >
                  <span class="i-lucide-play size-4" />
                </span>
              </div>
              <div
                v-else
                class="flex items-center gap-2 px-2.5 py-2 text-[11.5px] font-semibold text-[#111b21]"
              >
                <span
                  class="grid size-7 shrink-0 place-items-center rounded-lg bg-[#e7f3ef] text-[#128c7e]"
                >
                  <span class="i-lucide-file-text size-3.5" />
                </span>
                <span class="truncate" dir="ltr">{{ replyFile.filename }}</span>
              </div>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              v-if="buttonReplyContent.trim()"
              :class="BUBBLE"
              v-html="render(buttonReplyContent)"
            />
            <p
              v-if="!replyFile && !buttonReplyContent.trim()"
              class="rounded-2xl border border-dashed border-[#c9c1b5] px-2.5 py-1.5 text-[11px] font-semibold text-[#6b7280]"
            >
              {{ t('CANNED_MGMT.PREVIEW.REPLY_PLACEHOLDER') }}
            </p>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
